import React from 'react';
import { resolveFirstListingImage, LISTING_IMAGE_FALLBACK, resolveListingTitle } from '../../utils';
import './PrivateMessages.css';

interface User {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  profilePictureUrl?: string;
}

interface Listing {
  id: number;
  make?: string;
  model?: string;
  year?: number;
  trim?: string;
  identityDisplayTitle?: string | null;
  price?: string | number;
  imageLoc?: any;
}

interface Message {
  id: number;
  content: string;
  senderId: number;
  readAt: string | null;
  createdAt: string;
}

interface Conversation {
  id: number;
  listingId: number;
  buyerId: number;
  sellerId: number;
  createdAt: string;
  updatedAt: string;
  carsForSaleByListingId?: Listing | null;
  userByBuyerId?: User | null;
  userBySellerId?: User | null;
  /** JWT-scoped computed field from myConversations */
  latestMessage?: Message | null;
  /** Legacy nested shape (unused after secure read migration) */
  privateMessagesByConversationId?: {
    nodes: Message[];
  } | null;
}

interface ConversationListProps {
  conversations: Conversation[];
  currentUserId: number;
  selectedConversationId: number | null;
  onSelectConversation: (conversation: Conversation) => void;
  darkMode?: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentUserId,
  selectedConversationId,
  onSelectConversation,
  darkMode = false,
}) => {
  const getOtherUser = (conversation: Conversation): User => {
    const other =
      currentUserId === conversation.buyerId
        ? conversation.userBySellerId
        : conversation.userByBuyerId;
    return other || { id: 0, firstname: 'User' };
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstname && user.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    if (user.firstname) return user.firstname;
    return user.email?.split('@')[0] || 'User';
  };

  const getFirstImageUrl = (imageLoc: any): string =>
    resolveFirstListingImage(imageLoc, { darkMode });

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  const getLastMessage = (conversation: Conversation): Message | null => {
    if (conversation.latestMessage) return conversation.latestMessage;
    const messages = conversation.privateMessagesByConversationId?.nodes;
    return messages && messages.length > 0 ? messages[0] : null;
  };

  const hasUnread = (conversation: Conversation): boolean => {
    const lastMessage = getLastMessage(conversation);
    if (!lastMessage) return false;
    return lastMessage.senderId !== currentUserId && !lastMessage.readAt;
  };

  if (conversations.length === 0) {
    return (
      <div className={`conversation-list-empty ${darkMode ? 'dark' : ''}`}>
        <p>No conversations yet</p>
        <span>Contact a seller on a listing you&apos;re interested in</span>
      </div>
    );
  }

  return (
    <div className={`conversation-list ${darkMode ? 'dark' : ''}`} data-testid="conversation-list">
      {conversations.map((conversation) => {
        const otherUser = getOtherUser(conversation);
        const listing = conversation.carsForSaleByListingId;
        const lastMessage = getLastMessage(conversation);
        const unread = hasUnread(conversation);
        const isSelected = selectedConversationId === conversation.id;
        const listingTitle = resolveListingTitle(listing);

        return (
          <button
            type="button"
            key={conversation.id}
            className={`conversation-item ${isSelected ? 'selected' : ''} ${unread ? 'unread' : ''}`}
            onClick={() => onSelectConversation(conversation)}
            data-testid={`conversation-item-${conversation.id}`}
          >
            <div className="conversation-image">
              <img
                src={getFirstImageUrl(listing?.imageLoc)}
                alt={listingTitle || 'Listing'}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = LISTING_IMAGE_FALLBACK;
                }}
              />
            </div>
            <div className="conversation-details">
              <div className="conversation-header">
                <span className="conversation-user-name">
                  {getUserDisplayName(otherUser)}
                </span>
                {(lastMessage || conversation.updatedAt) && (
                  <span className="conversation-time">
                    {formatTime(lastMessage?.createdAt || conversation.updatedAt)}
                  </span>
                )}
              </div>
              <div className="conversation-listing-title">{listingTitle || 'Vehicle listing'}</div>
              {lastMessage ? (
                <div className="conversation-preview">
                  {lastMessage.senderId === currentUserId ? 'You: ' : ''}
                  {lastMessage.content.length > 50
                    ? `${lastMessage.content.substring(0, 50)}…`
                    : lastMessage.content}
                </div>
              ) : (
                <div className="conversation-preview muted">No messages yet</div>
              )}
            </div>
            {unread && <div className="unread-indicator" aria-label="Unread" />}
          </button>
        );
      })}
    </div>
  );
};

export default ConversationList;
